/**
 * LoginPage — Phase 45.1 (Forgot Password in-app flow)
 *
 * Two modes rendered on the same page, toggled by `mode` state:
 *   'login'  — normal email + password sign-in
 *   'forgot' — email field + "שלח קישור לאיפוס" button
 *
 * Forgot-password flow:
 *   1. User clicks "Forgot password?" → switches to 'forgot' mode
 *   2. Enters email → we call supabase.auth.resetPasswordForEmail(email, {
 *        redirectTo: window.location.origin + '/update-password'
 *      })
 *      This tells Supabase exactly where to send the user after they click
 *      the link in the email.
 *   3. We show a success confirmation regardless of whether the email exists
 *      (prevents email enumeration).
 *   4. User clicks the link in the email → Supabase redirects to
 *      /update-password with #access_token in the URL fragment.
 *   5. AuthContext intercepts PASSWORD_RECOVERY, PasswordRecoveryWatcher
 *      navigates to /update-password, UpdatePasswordPage handles the rest.
 *
 * IMPORTANT — Supabase dashboard setting required:
 *   Authentication → URL Configuration → Redirect URLs
 *   Add: https://axelio.biz/update-password
 *   (The redirectTo in the API call must match an allowed redirect URL)
 */

import { useState, FormEvent } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui';
import { Mail, Lock, CircleAlert as AlertCircle, ShieldCheck, Eye, EyeOff, CircleCheck as CheckCircle2, ArrowRight, KeyRound } from 'lucide-react';
import { INVITE_TOKEN_KEY } from '@/pages/InviteLandingPage';

type Mode = 'login' | 'forgot' | 'forgot_sent';

export function LoginPage() {
  const navigate       = useNavigate();
  const location       = useLocation();
  const [searchParams] = useSearchParams();
  const { signIn }     = useAuth();

  // ── Form state ─────────────────────────────────────────────────────────────
  const [mode,         setMode]         = useState<Mode>('login');
  const [email,        setEmail]        = useState('');
  const [password,     setPassword]     = useState('');
  const [error,        setError]        = useState<string | null>(null);
  const [loading,      setLoading]      = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe,   setRememberMe]   = useState(() =>
    localStorage.getItem('axelio-remember-me') !== 'false',
  );

  // ── Routing helpers ────────────────────────────────────────────────────────
  const inviteToken =
    searchParams.get('invite') ||
    sessionStorage.getItem(INVITE_TOKEN_KEY) ||
    '';

  const locationState  = location.state as { redirectTo?: string; message?: string } | null;
  const redirectTo     = locationState?.redirectTo;
  // Phase 45: success message sent from UpdatePasswordPage after a password reset
  const successMessage = locationState?.message ?? null;

  // No guard — /login is always accessible directly.

  // ── Sign-in submit ─────────────────────────────────────────────────────────
  const handleSignIn = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (!email || !password) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      const { error } = await signIn(email, password, rememberMe);
      if (error) {
        setError(error.message);
      } else if (inviteToken) {
        navigate(`/join/${inviteToken}`, { replace: true });
      } else if (redirectTo) {
        navigate(redirectTo, { replace: true });
      } else {
        navigate('/workspaces', { replace: true });
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Forgot-password submit ─────────────────────────────────────────────────
  const handleForgotPassword = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('נא להזין כתובת אימייל');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('כתובת אימייל אינה תקינה');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        // ⚠️ This redirectTo MUST be listed under:
        // Supabase dashboard → Authentication → URL Configuration → Redirect URLs
        redirectTo: `${window.location.origin}/update-password`,
      });

      if (error) throw error;

      // Always show success (prevents email enumeration)
      setMode('forgot_sent');
    } catch (err: unknown) {
      setError((err as { message?: string })?.message ?? 'שגיאה בשליחת הקישור — נסה שוב');
    } finally {
      setLoading(false);
    }
  };

  // ── Render: forgot_sent confirmation ───────────────────────────────────────
  if (mode === 'forgot_sent') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50
                      flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 mb-4">
              <img src="/logo.svg" alt="Axelio Logo" className="w-full h-full drop-shadow-lg" />
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center space-y-4">
            <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto" />
            <h2 className="text-xl font-bold text-gray-900">הקישור נשלח!</h2>
            <p className="text-sm text-gray-600">
              שלחנו קישור לאיפוס סיסמה לכתובת{' '}
              <span className="font-semibold text-gray-800" dir="ltr">{email}</span>.
            </p>
            <p className="text-xs text-gray-400">
              לא קיבלת? בדוק את תיקיית הספאם, או{' '}
              <button
                onClick={() => { setMode('forgot'); setError(null); }}
                className="text-primary-600 hover:underline font-medium"
              >
                נסה שוב
              </button>
              .
            </p>
            <button
              onClick={() => { setMode('login'); setError(null); }}
              className="mt-2 inline-flex items-center gap-1.5 text-sm text-primary-600
                         hover:text-primary-700 font-medium"
            >
              <ArrowRight className="w-4 h-4" />
              חזרה להתחברות
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: forgot form ────────────────────────────────────────────────────
  if (mode === 'forgot') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50
                      flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 mb-4">
              <img src="/logo.svg" alt="Axelio Logo" className="w-full h-full drop-shadow-lg" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">שכחת סיסמה?</h1>
            <p className="text-gray-500 text-sm">
              הזן את האימייל שלך ונשלח קישור לאיפוס הסיסמה
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 space-y-5"
               dir="rtl">

            {error && (
              <div className="flex items-start gap-2.5 p-3.5 bg-red-50 border border-red-200
                              rounded-lg text-sm text-red-700">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleForgotPassword} className="space-y-4" noValidate>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  כתובת אימייל
                </label>
                <div className="relative">
                  <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5
                                   text-gray-400 pointer-events-none" />
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    dir="ltr"
                    autoFocus
                    className="w-full pr-11 pl-4 py-3 border border-gray-300 rounded-lg
                               focus:ring-2 focus:ring-primary-500 focus:border-transparent
                               transition-all"
                    disabled={loading}
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full py-3 text-base font-semibold"
                isLoading={loading}
                disabled={loading}
              >
                {loading ? 'שולח…' : 'שלח קישור לאיפוס סיסמה'}
              </Button>
            </form>

            <button
              onClick={() => { setMode('login'); setError(null); }}
              className="w-full flex items-center justify-center gap-1.5 text-sm
                         text-gray-500 hover:text-gray-700 font-medium mt-2"
            >
              <ArrowRight className="w-4 h-4" />
              חזרה להתחברות
            </button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500 flex items-center justify-center gap-2">
              <ShieldCheck className="w-4 h-4" />
              Protected by bank-grade 256-bit SSL encryption
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: normal login form ──────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50
                    flex items-center justify-center p-4">
      <div className="w-full max-w-md">

        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 mb-4">
            <img src="/logo.svg" alt="Axelio Logo" className="w-full h-full drop-shadow-lg" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome to Axelio</h1>
          <p className="text-gray-600">Work Smarter. Grow Faster.</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Sign In</h2>
            <p className="text-gray-600 text-sm">Enter your credentials to access your account</p>

            {/* Module Badge */}
            {redirectTo && (
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-2 bg-blue-50
                              border border-blue-200 rounded-lg">
                <ShieldCheck className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-semibold text-blue-700">
                  {redirectTo.includes('cashflow')  && 'Logging into Cash Flow Module'}
                  {redirectTo.includes('inventory') && 'Logging into Inventory Module'}
                  {redirectTo.includes('hr')        && 'Logging into HR/Employees Module'}
                </span>
              </div>
            )}
          </div>

          {/* Phase 45: post-password-reset success banner */}
          {successMessage && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg
                            flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
              <p className="text-sm font-medium text-green-800">{successMessage}</p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg
                            flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">Authentication Failed</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSignIn} className="space-y-5">

            {/* Email */}
            <div>
              <label htmlFor="email"
                     className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg
                             focus:ring-2 focus:ring-primary-500 focus:border-transparent
                             transition-all"
                  disabled={loading}
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password"
                     className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-11 pr-12 py-3 border border-gray-300 rounded-lg
                             focus:ring-2 focus:ring-primary-500 focus:border-transparent
                             transition-all"
                  disabled={loading}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400
                             hover:text-gray-600 transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Remember me + Forgot password */}
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center cursor-pointer gap-2">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={e => {
                    setRememberMe(e.target.checked);
                    localStorage.setItem('axelio-remember-me', String(e.target.checked));
                  }}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded
                             focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-gray-700">Remember me</span>
              </label>

              {/* ── Phase 45.1: real forgot-password link ─────────────────── */}
              <button
                type="button"
                onClick={() => { setMode('forgot'); setError(null); }}
                className="text-primary-600 hover:text-primary-700 font-medium
                           flex items-center gap-1 transition-colors"
              >
                <KeyRound className="w-3.5 h-3.5" />
                Forgot password?
              </button>
            </div>

            <Button
              type="submit"
              className="w-full py-3 text-base font-semibold"
              isLoading={loading}
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">Don't have an account?</span>
            </div>
          </div>

          {/* Sign Up */}
          <Link to={inviteToken ? `/signup?invite=${inviteToken}` : '/signup'}>
            <Button type="button" variant="outline" className="w-full py-3 text-base font-semibold">
              Create Account
            </Button>
          </Link>
        </div>

        {/* Security notice */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500 flex items-center justify-center gap-2">
            <ShieldCheck className="w-4 h-4" />
            Protected by bank-grade 256-bit SSL encryption
          </p>
        </div>

        {/* Privacy policy link */}
        <div className="mt-3 text-center space-y-2">
          <Link to="/privacy" className="text-xs text-gray-400 hover:text-gray-600 hover:underline">
            Privacy Policy
          </Link>
          <div className="flex items-center justify-center gap-4">
            <Link to="/employee" className="text-xs text-gray-400 hover:text-gray-600 hover:underline">
              Employee Portal
            </Link>
            <Link to="/b2b" className="text-xs text-gray-400 hover:text-gray-600 hover:underline">
              B2B Portal
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
